import { CancellationToken } from "../Promises/CancellationTokenSource";
import { IAsyncTree, IBaseAsyncTreeChildrenResult, IBaseAsyncTreeVisitorResult, ITraverseProcessDetails } from "./IAsyncTree";

/**
 * Jednoduchá generická stromová struktura umožňující synchronní a asynchronní procházení jednotlivých uzlů
 */
export class AsyncTree<T> implements IAsyncTree<T> {
    public readonly element: T | null;
    protected readonly _children: this[];

    public get children(): Iterable<this> {
        return this._getIterator();
    }

    public getChildren(ct?: CancellationToken): Iterable<this> {
        return this._getIterator(ct);
    }

    constructor(element: T | null = null) {
        this.element = element;
        this._children = [];
    }

    protected *_getIterator(_ct?: CancellationToken): IterableIterator<this> {
        for (const a of this._children)
            yield a;
    }

    public add(element: T): this {
        const node = new AsyncTree<T>(element) as this;
        return this.addNode(node);
    }

    public addRange(elements: T[]): this {
        for (const element of elements) {
            const node = new AsyncTree<T>(element) as this;
            this.addNode(node);
        }
        return this;
    }

    public addNode(node: this): this {
        this._children.push(node);
        return node;
    }

    public addNodeRange(nodes: this[]): this {
        for (const node of nodes) {
            this.addNode(node);
        }
        return this;
    }

    public addToNode(element: T, parent: T): this | null {
        const parentNode: this = this.findByElement(parent);
        if (parentNode)
            return parentNode.add(element);
        else
            return this.add(element);
    }

    public hasChildNode(node: this): boolean {
        return this._children.includes(node);
    }

    public removeChildNode(node: this): boolean {
        const index = this._children.indexOf(node);
        if (index < 0)
            return false;

        if (this._children.splice(index, 1).length > 0)
            return true;

        return false;
    }

    public findByElement(element: T): this | null {
        return AsyncTree._findDFS<T, this>(this, (el) => el === element);
    }

    public removeNode(nodeToRemove: this): boolean {
        const parentNode = AsyncTree._findNodeDFS<T, this>(this, (parent) => parent.hasChildNode(nodeToRemove));
        if (parentNode) {
            return parentNode.removeChildNode(nodeToRemove);
        }
        return false;
    }

    public replaceNode(nodeToAdd: this): this {
        const parentNode = AsyncTree._findNodeDFS<T, this>(this, (parent) => parent.hasChildNode(this));
        if (parentNode) {
            parentNode.removeChildNode(this);
            return parentNode.addNode(nodeToAdd);
        }
        return nodeToAdd;
    }

    public traverseDFS(visitor: (data: T | null) => void): void {
        visitor(this.element);
        for (const child of this.children)
            child.traverseDFS(visitor);
    }

    private static _findDFS<TI, TT extends AsyncTree<TI>>(node: TT, predicate: (data: TI | null) => boolean): TT | null {
        return AsyncTree._findNodeDFS(node, (node: TT) => {
            return predicate(node.element);
        });
    }

    protected static _findNodeDFS<TI, TT extends AsyncTree<TI>>(node: TT, predicate: (searchedNode: TT | null) => boolean): TT | null {
        if (predicate(node))
            return node;

        for (const c of node.children) {
            const found = AsyncTree._findNodeDFS<TI, TT>(c, predicate);
            if (found)
                return found;
        }

        return null;
    }

    public async traverseRootsFinishFirstAsync<TVisitResult = IBaseAsyncTreeVisitorResult, TTraverseProcessDetails = ITraverseProcessDetails>(
        visitor: (data: T | null, node: IAsyncTree<T>, traverseProcessDetails?: TTraverseProcessDetails) => Promise<TVisitResult>,
        traverseProcessDetails?: TTraverseProcessDetails,
        processChildrenPrecondition?: (visitResult: TVisitResult) => boolean,
        failedPreconditionAction?: (visitResult: TVisitResult) => boolean,
        afterChildrenMethod?: (element: T | null, childrenResult: IBaseAsyncTreeChildrenResult, visitorResult: TVisitResult) => TVisitResult /*TODO-rites: volitelná funkce, která se provede po zpracování potomků a umožní třeba skrytí kontejneru při neviditelnosti všech potomků; c2: přijímá i výsledek rodiče a může jej upravit*/,
        finallyMethod?: (data: T | null) => void,
        ct?: CancellationToken,
    ): Promise<TVisitResult> {
        let visitResult: TVisitResult | void = {} as TVisitResult;
        const childrenResult: IBaseAsyncTreeChildrenResult = { results: [] };
        let processChildren: boolean = true;

        try {
            if (this.element !== null)
                visitResult = await visitor(this.element, this, traverseProcessDetails);
            if (processChildrenPrecondition && !processChildrenPrecondition(visitResult))
                if (!failedPreconditionAction || !failedPreconditionAction(visitResult)) // Pokud je nastavena akce při neúspěchu prekondice a vrátí true, pokračuje vyhodnocování, jinak se skončí
                    processChildren = false;

            if (processChildren) {
                childrenResult.results = await Promise.all(
                    Array.from(this.getChildren(ct)).map(child => child.traverseRootsFinishFirstAsync(
                        visitor,
                        (visitResult instanceof IBaseAsyncTreeVisitorResult) ? visitResult.traverseDetails : undefined,
                        processChildrenPrecondition,
                        failedPreconditionAction,
                        afterChildrenMethod,
                        finallyMethod,
                        ct,
                    )),
                );

                if (afterChildrenMethod)
                    visitResult = afterChildrenMethod(this.element, childrenResult, visitResult);
            }
        } finally {
            if (finallyMethod)
                finallyMethod(this.element);
        }

        return visitResult;
    }
}
